import React, { useEffect, useMemo, useRef, useState } from "react";
import { MoreHorizontal, PenSquare, Pencil, Search, Trash2, X } from "lucide-react";
import "./LAEAI.css";

/**
 * Historial de conversaciones de "Tu IA Normativa" (nueva).
 * Agrupado por fecha (Hoy, Ayer, Últimos 7 días, Últimos 30 días, Anteriores), con buscador,
 * y en cada conversación un menú para renombrarla (en línea) o eliminarla.
 */
const GRUPOS = ["today", "yesterday", "last7", "last30", "older"];
const DIA_MS = 24 * 60 * 60 * 1000;

const inicioDelDia = (fecha) => {
  const d = new Date(fecha);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const grupoDe = (fecha, hoy) => {
  const dias = Math.round((hoy - inicioDelDia(fecha)) / DIA_MS);
  if (dias <= 0) return "today";
  if (dias === 1) return "yesterday";
  if (dias < 7) return "last7";
  if (dias < 30) return "last30";
  return "older";
};

// Para buscar sin tener en cuenta mayúsculas ni tildes
const MARCAS = new RegExp(`[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`, "g"); // tildes y diéresis tras NFD
const normalizar = (texto = "") => texto.normalize("NFD").replace(MARCAS, "").toLowerCase();

const ElementoConversacion = ({ conversacion, activa, bloqueado, onSeleccionar, onRenombrar, onEliminar, t }) => {
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [editando, setEditando] = useState(false);
  const [titulo, setTitulo] = useState(conversacion.titulo);
  const refMenu = useRef(null);
  const refInput = useRef(null);

  useEffect(() => setTitulo(conversacion.titulo), [conversacion.titulo]);

  // Cerrar el menú al hacer clic fuera
  useEffect(() => {
    if (!menuAbierto) return undefined;
    const alClicar = (e) => { if (!refMenu.current?.contains(e.target)) setMenuAbierto(false); };
    document.addEventListener("mousedown", alClicar);
    return () => document.removeEventListener("mousedown", alClicar);
  }, [menuAbierto]);

  useEffect(() => {
    if (editando) refInput.current?.select();
  }, [editando]);

  const guardar = async () => {
    const limpio = titulo.replace(/\s+/g, " ").trim();
    setEditando(false);
    if (!limpio || limpio === conversacion.titulo) {
      setTitulo(conversacion.titulo);
      return;
    }
    const ok = await onRenombrar(conversacion.id, limpio);
    if (!ok) setTitulo(conversacion.titulo);
  };

  if (editando) {
    return (
      <div className="px-1 py-0.5">
        <input
          ref={refInput}
          value={titulo}
          maxLength={150}
          onChange={(e) => setTitulo(e.target.value)}
          onBlur={guardar}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); guardar(); }
            if (e.key === "Escape") { setTitulo(conversacion.titulo); setEditando(false); }
          }}
          className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm text-gray-900 outline-none focus:border-gray-800"
        />
      </div>
    );
  }

  return (
    <div
      ref={refMenu}
      className={`group relative flex items-center rounded-lg transition-colors ${
        activa ? "bg-gray-100" : "hover:bg-gray-50"
      }`}
    >
      <button
        type="button"
        onClick={() => onSeleccionar(conversacion.id)}
        disabled={bloqueado && !activa}
        title={conversacion.titulo}
        className={`min-w-0 flex-1 truncate rounded-lg border-none bg-transparent px-3 py-2.5 text-left text-sm ${
          activa ? "font-medium text-gray-900" : "text-gray-700"
        } ${bloqueado && !activa ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
      >
        {conversacion.titulo}
      </button>
      <button
        type="button"
        onClick={() => setMenuAbierto((v) => !v)}
        disabled={bloqueado}
        title={t("laeai.history.options")}
        aria-label={t("laeai.history.options")}
        className={`mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border-none bg-transparent transition-colors hover:bg-gray-200 hover:text-gray-900 ${
          menuAbierto || activa ? "laeai-opciones laeai-opciones--visible text-gray-700" : "laeai-opciones text-gray-500"
        } ${bloqueado ? "cursor-default" : "cursor-pointer"}`}
      >
        <MoreHorizontal size={16} />
      </button>
      {menuAbierto && (
        <div className="absolute right-1 top-full z-30 mt-1 w-40 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          <button
            type="button"
            onClick={() => { setMenuAbierto(false); setEditando(true); }}
            className="flex w-full cursor-pointer items-center gap-2 border-none bg-transparent px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
          >
            <Pencil size={14} /> {t("laeai.history.rename")}
          </button>
          <button
            type="button"
            onClick={() => { setMenuAbierto(false); onEliminar(conversacion); }}
            className="flex w-full cursor-pointer items-center gap-2 border-none bg-transparent px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
          >
            <Trash2 size={14} /> {t("laeai.history.delete")}
          </button>
        </div>
      )}
    </div>
  );
};

export default function LAEAIHistorial({
  conversaciones, activaId, cargando, error, bloqueado, onSeleccionar, onNueva, onRenombrar, onEliminar, onCerrar, t,
}) {
  const [busqueda, setBusqueda] = useState("");

  const grupos = useMemo(() => {
    const filtro = normalizar(busqueda.trim());
    const hoy = inicioDelDia(new Date());
    const porGrupo = Object.fromEntries(GRUPOS.map((g) => [g, []]));
    conversaciones
      .filter((c) => !filtro || normalizar(c.titulo).includes(filtro))
      .forEach((c) => porGrupo[grupoDe(c.ultimo_mensaje_at || c.creada_at, hoy)].push(c));
    return GRUPOS.map((g) => ({ clave: g, conversaciones: porGrupo[g] })).filter((g) => g.conversaciones.length);
  }, [conversaciones, busqueda]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between px-4 pb-2 pt-4">
        <h2 className="m-0 text-base font-bold text-gray-900">{t("laeai.history.title")}</h2>
        {onCerrar && (
          <button
            type="button"
            onClick={onCerrar}
            aria-label={t("laeai.history.close")}
            className="cursor-pointer border-none bg-transparent p-1 text-gray-400 hover:text-gray-700"
          >
            <X size={18} />
          </button>
        )}
      </div>

      <div className="px-3 pb-2">
        <button
          type="button"
          onClick={onNueva}
          disabled={bloqueado}
          className={`mb-2 flex w-full items-center gap-2 rounded-xl bg-transparent px-3 py-2.5 text-sm text-gray-700 transition-all duration-150 ${
            bloqueado ? "cursor-default opacity-60" : "cursor-pointer hover:bg-gray-100 hover:text-gray-900"
          }`}
          style={{ border: "1px solid #e5e7eb" }}
        >
          <PenSquare size={16} />
          {t("laeai.history.newChat")}
        </button>
        <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-2.5 py-1.5" style={{ border: "1px solid #e5e7eb" }}>
          <Search size={14} className="shrink-0 text-gray-400" />
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder={t("laeai.history.search")}
            className="w-full border-none bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-400"
          />
          {busqueda && (
            <button type="button" onClick={() => setBusqueda("")} className="cursor-pointer border-none bg-transparent p-0 text-gray-400 hover:text-gray-700">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      <div style={{ borderBottom: "1px solid #e5e7eb" }} />

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3 pt-2">
        {cargando && !conversaciones.length ? (
          <div className="space-y-2 px-2 pt-2">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-8 animate-pulse rounded-lg bg-gray-100" />)}
          </div>
        ) : error ? (
          <p className="px-3 py-2 text-sm text-red-600">{t("laeai.history.loadError")}</p>
        ) : !conversaciones.length ? (
          <p className="px-3 py-2 text-sm text-gray-500">{t("laeai.history.empty")}</p>
        ) : !grupos.length ? (
          <p className="px-3 py-2 text-sm text-gray-500">{t("laeai.history.noResults")}</p>
        ) : (
          grupos.map((grupo) => (
            <div key={grupo.clave} className="mb-3">
              <p className="m-0 px-3 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                {t(`laeai.history.${grupo.clave}`)}
              </p>
              <div className="flex flex-col gap-0.5">
                {grupo.conversaciones.map((c) => (
                  <ElementoConversacion
                    key={c.id}
                    conversacion={c}
                    activa={c.id === activaId}
                    bloqueado={bloqueado}
                    onSeleccionar={onSeleccionar}
                    onRenombrar={onRenombrar}
                    onEliminar={onEliminar}
                    t={t}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
